/**
 * Date format guards for OpenAPI `format: date-time` query parameters.
 *
 * The canonical UCC fee-reconciliation spec
 * (schema-registry/ucc-fee-reconciliation.openapi.yaml) declares every
 * temporal field as `format: date-time` (RFC 3339 / ISO 8601, Zulu).
 * The backend Zod validator at
 * staging/aaim-core-new/services/ucc-fee-reconciliation/src/routes/payment-routes.ts
 * enforces this via `z.string().datetime()`, which accepts only the
 * `YYYY-MM-DDTHH:MM:SS[.sss]Z` shape and rejects bare `YYYY-MM-DD`.
 *
 * Callers MUST construct values via `new Date(...).toISOString()` (or
 * an equivalent producer). This module surfaces the contract at the
 * hook boundary so a mistyped probe or future `<input type="date">`
 * wiring fails LOUD on the SPA side instead of becoming an opaque
 * 400 INVALID_QUERY_PARAMS at the gateway.
 *
 * Reference: `.claude/rules/openapi-first.md`, F-UCC-FEE-DATE-FORMAT-CONTRACT.
 */

/**
 * Strict ISO 8601 UTC datetime shape, Zulu suffix required.
 * Matches what `z.string().datetime()` accepts on the backend.
 */
const ISO_8601_UTC_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/;

/**
 * Assert that `value` is a strict ISO 8601 UTC datetime string.
 *
 * Throws `Error` with a message naming `fieldName` if the value does
 * not match the canonical `YYYY-MM-DDTHH:MM:SSZ` (optionally with
 * fractional seconds) shape. The thrown error is a development-time
 * defect signal -- production code paths should always be supplying
 * `.toISOString()` output and never trip the guard.
 *
 * @param value the string to validate
 * @param fieldName the parameter name to include in the error message
 */
export function assertIso8601Datetime(value: string, fieldName: string): void {
  if (!ISO_8601_UTC_DATETIME.test(value)) {
    throw new Error(
      `${fieldName} must be an ISO 8601 UTC datetime (e.g. 2026-01-01T00:00:00Z); ` +
        `received '${value}'. Use new Date(...).toISOString() at the call site.`,
    );
  }
}
